import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Dialog} from "primereact/dialog";
import {Button} from "primereact/button";
import {Checkbox} from "primereact/checkbox";
import {InputText} from "primereact/inputtext";
import {TabPanel, TabView} from "primereact/tabview";
import {ProgressSpinner} from "primereact/progressspinner";
import {useDispatch} from "react-redux";
import {useTranslate} from "../../../system/useTranslate";
import Icon from "../../../system/components/icon";
import Avatar from "../../../system/components/avatar";
import {setMessage} from "../../../system/app-slice";

const statusClass = {
    todo: "bg-blue-50 text-blue-600 border-blue-200",
    doing: "bg-orange-50 text-orange-600 border-orange-200",
    blocked: "bg-red-50 text-red-600 border-red-200",
    done: "bg-green-50 text-green-600 border-green-200",
};

const faNumberFormat = new Intl.NumberFormat("fa-IR");

function SelectTasksDialog({visible, setVisible, projectTasks, deliverableTasks, onChange, milestoneTitle, loading, error, onHide}) {
    const translate = useTranslate();
    const dispatch = useDispatch();

    const [searchText, setSearchText] = useState("");
    const [selectedAvailable, setSelectedAvailable] = useState(new Set());
    const [removedTasks, setRemovedTasks] = useState(new Set());
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (visible) {
            setSearchText("");
            setSelectedAvailable(new Set());
            setRemovedTasks(new Set());
            setSubmitting(false);
        }
    }, [visible]);

    const safeAvailable = Array.isArray(projectTasks) ? projectTasks : [];
    const safeLinked = Array.isArray(deliverableTasks) ? deliverableTasks : [];

    const filterTasks = useCallback((tasks) => {
        const query = searchText.trim().toLowerCase();
        if (!query) return tasks;
        return tasks.filter(task =>
            task.title?.toLowerCase().includes(query) ||
            task.projects?.some(project => project.title?.toLowerCase().includes(query)) ||
            task.assignee?.member?.fullname?.toLowerCase().includes(query)
        );
    }, [searchText]);

    const allAvailable = useMemo(
        () => [...safeAvailable, ...safeLinked.filter(task => removedTasks.has(task.id))],
        [safeAvailable, safeLinked, removedTasks]
    );
    const allLinked = useMemo(
        () => safeLinked.filter(task => !removedTasks.has(task.id)),
        [safeLinked, removedTasks]
    );
    const availableTasks = useMemo(() => filterTasks(allAvailable), [allAvailable, filterTasks]);
    const linkedTasks = useMemo(() => filterTasks(allLinked), [allLinked, filterTasks]);

    const selectedCount = selectedAvailable.size;

    const toggleAvailable = useCallback((task) => {
        setSelectedAvailable(prev => {
            const next = new Set(prev);
            if (next.has(task.id)) next.delete(task.id);
            else next.add(task.id);
            return next;
        });
    }, []);

    const removeFromLinked = useCallback((task) => {
        setRemovedTasks(prev => {
            const next = new Set(prev);
            next.add(task.id);
            return next;
        });
    }, []);

    const closeDialog = () => {
        if (typeof onHide === 'function') onHide();
        else if (typeof setVisible === 'function') setVisible(false);
    };

    const handleConfirm = async () => {
        if (submitting) return;
        setSubmitting(true);
        try {
            const finalTasks = [
                ...safeLinked.filter(task => !removedTasks.has(task.id)),
                ...safeAvailable.filter(task => selectedAvailable.has(task.id)),
            ];
            await onChange?.(finalTasks);
            dispatch(setMessage({severity: "success", content: translate("tasks-linked-successfully")}));
            closeDialog();
        } catch (e) {
            dispatch(setMessage({severity: "error", content: translate("tasks-link-error")}));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog
            showCloseIcon={false}
            pt={{
                root: () => ({className: "rtl"}),
                header: () => ({className: "flex-shrink-0"}),
                content: () => ({className: "p-0 flex flex-column overflow-hidden flex-1 min-h-0"}),
                footer: () => ({className: "p-0 flex-shrink-0"}),
            }}
            style={{height: "85vh", maxHeight: "85vh", display: "flex", flexDirection: "column"}}
            className="w-11 sm:w-9 md:w-7 lg:w-5 border-round-2xl overflow-hidden"
            header={<Header milestoneTitle={milestoneTitle} onClose={closeDialog}/>}
            footer={<Footer selectedCount={selectedCount} submitting={submitting} onCancel={closeDialog} onConfirm={handleConfirm}/>}
            visible={visible}
            onHide={closeDialog}
        >
            <div className="flex flex-column h-full min-h-0">
                <div className="p-3 border-bottom-1 border-200 flex-shrink-0">
                    <SearchField value={searchText} onChange={setSearchText} onClear={() => setSearchText("")}/>
                </div>
                <TabView
                    className="flex-1 flex flex-column min-h-0 overflow-hidden text-xs"
                    pt={{
                        nav: () => ({className: "flex-shrink-0"}),
                        panelcontainer: () => ({className: "flex-1 min-h-0 overflow-hidden"}),
                    }}
                >
                    <TabPanel pt={{content: () => ({className: "h-full overflow-hidden"})}} header={`${translate("milestone-task-available-tab")} (${faNumberFormat.format(allAvailable.length)})`}>
                        <TasksList
                            tasks={availableTasks}
                            loading={loading}
                            error={error}
                            selected={selectedAvailable}
                            onToggle={toggleAvailable}
                            mode="available"
                        />
                    </TabPanel>
                    <TabPanel pt={{content: () => ({className: "h-full overflow-hidden"})}} header={`${translate("milestone-task-linked-tab")} (${faNumberFormat.format(allLinked.length)})`}>
                        <TasksList
                            tasks={linkedTasks}
                            loading={loading}
                            error={error}
                            selected={removedTasks}
                            onToggle={removeFromLinked}
                            mode="linked"
                        />
                    </TabPanel>
                </TabView>
            </div>
        </Dialog>
    );
}

function SearchField({value, onChange, onClear}) {
    const translate = useTranslate();
    return (
        <div className="relative w-full">
            <i className="pi pi-search text-400 absolute left-0 top-0 mt-3 ml-3 text-sm" aria-hidden="true"/>
            <InputText
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="w-full border-round-lg text-sm pl-5 pr-5"
                style={{paddingTop: "0.65rem", paddingBottom: "0.65rem"}}
                placeholder={translate("search-tasks-in-project")}
            />
            {value && (
                <Icon
                    icon="mi mi:close"
                    className="text-base cursor-pointer text-400 absolute right-0 top-0 mt-3 mr-3"
                    onClick={onClear}
                    ariaLabel={translate("clear-search")}
                />
            )}
        </div>
    );
}

const TasksList = React.memo(function TasksList({tasks, loading, error, selected, onToggle, mode}) {
    const translate = useTranslate();

    if (loading) {
        return (
            <div className="flex flex-column align-items-center justify-content-center gap-2 p-8 text-xs text-500 h-full">
                <ProgressSpinner className="w-2rem h-2rem" strokeWidth="4"/>
                <span>{translate("loading-tasks")}</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex flex-column align-items-center justify-content-center gap-2 p-8 text-xs text-red-500 h-full">
                <Icon icon="mi mi:error" className="text-3xl text-300"/>
                <span>{translate("tasks-load-error")}</span>
            </div>
        );
    }

    if (tasks.length === 0) {
        return (
            <div className="flex flex-column align-items-center justify-content-center gap-2 p-8 text-xs text-500 h-full">
                <Icon icon="mi mi:search_off" className="text-3xl text-300"/>
                <span>{translate("no-tasks-found")}</span>
            </div>
        );
    }

    return (
        <div className="h-full overflow-y-auto flex flex-column gap-2 p-3">
            {tasks.map(task => (
                <TaskCard
                    key={task.id ?? task.documentId}
                    task={task}
                    checked={selected.has(task.id)}
                    onToggle={onToggle}
                    mode={mode}
                />
            ))}
        </div>
    );
});

const TaskCard = React.memo(function TaskCard({task, checked, onToggle, mode}) {
    const translate = useTranslate();
    const projects = task.projects?.map(project => project.title).filter(Boolean) || [];
    const assignee = task.assignee?.member;
    const isLinked = mode === "linked";

    return (
        <div
            onClick={isLinked ? undefined : () => onToggle(task)}
            className={`flex align-items-center gap-3 p-3 border-1 border-round-xl transition-duration-200 ${
                isLinked
                    ? "border-200 bg-surface-50"
                    : checked
                        ? "border-primary bg-white"
                        : "border-200 bg-white hover:border-300 cursor-pointer"
            }`}
        >
            {!isLinked && <Checkbox checked={checked} onChange={(e) => {e.stopPropagation(); onToggle(task);}}/>}
            <div className="flex-1 min-w-0 flex flex-column gap-1">
                <span className="text-sm text-900 font-medium overflow-hidden text-overflow-ellipsis white-space-nowrap">{task.title}</span>
                <div className="flex flex-wrap align-items-center gap-3 text-2xs text-500">
                    {projects.length > 0 && (
                        <span className="flex align-items-center gap-1">
                            <i className="pi pi-folder text-xs text-400"/>
                            <span className="overflow-hidden text-overflow-ellipsis white-space-nowrap max-w-6rem">{projects[0]}</span>
                        </span>
                    )}
                    {assignee && (
                        <span className="flex align-items-center gap-1">
                            <Avatar memberData={assignee} size="small" className="text-3xs" style={{width: "1.25rem", height: "1.25rem"}}/>
                            <span className="overflow-hidden text-overflow-ellipsis white-space-nowrap max-w-6rem">{assignee.fullname}</span>
                        </span>
                    )}
                </div>
            </div>
            {isLinked ? (
                <button
                    type="button"
                    onClick={(e) => {e.stopPropagation(); onToggle(task);}}
                    className="flex-shrink-0 w-2rem h-2rem border-circle border-1 border-200 bg-white text-500 flex align-items-center justify-content-center cursor-pointer hover:border-red-300 hover:bg-red-50 hover:text-red-500 transition-duration-150"
                    aria-label={translate("remove")}
                >
                    <i className="pi pi-trash text-xs"/>
                </button>
            ) : (
                <span className={`text-2xs font-bold px-2 py-1 border-round border-1 flex-shrink-0 ${statusClass[task.taskStatus] || "bg-surface-100 text-600 border-200"}`}>
                    {translate(task.taskStatus)}
                </span>
            )}
        </div>
    );
});

function Header({milestoneTitle, onClose}) {
    const translate = useTranslate();
    return (
        <div className="flex align-items-center justify-content-between gap-3 w-full">
            <div className="flex align-items-center gap-3">
                <span className="flex align-items-center justify-content-center w-3rem h-3rem border-circle bg-primary-50 text-primary flex-shrink-0">
                    <Icon icon="mi mi:add_task" className="text-xl"/>
                </span>
                <div className="flex flex-column">
                    <span className="text-sm font-bold text-900">{translate("milestone-task-select-title")}</span>
                    {milestoneTitle && <span className="text-2xs text-500">{milestoneTitle}</span>}
                </div>
            </div>
            <button
                type="button"
                onClick={onClose}
                className="flex-shrink-0 w-2rem h-2rem border-circle border-none bg-transparent flex align-items-center justify-content-center cursor-pointer hover:surface-100 transition-duration-150"
                aria-label={translate("close")}
            >
                <Icon icon="mi mi:close" className="text-lg text-500"/>
            </button>
        </div>
    );
}

function Footer({selectedCount, submitting, onCancel, onConfirm}) {
    const translate = useTranslate();
    return (
        <div className="flex flex-row-reverse align-items-center gap-2 justify-content-between p-3 surface-50 border-top-1 border-200 flex-shrink-0">
            <div className="flex align-items-center gap-2">
                <Button
                    disabled={submitting}
                    className="text-xs p-button-secondary p-button-outlined p-2 pl-3 pr-3 border-round-lg"
                    label={translate("cancel")}
                    onClick={onCancel}
                />
                <Button
                    className="text-xs gap-2 p-2 pl-3 pr-3 border-round-lg"
                    label={translate("confirm-and-link-tasks")}
                    icon={<Icon icon="mi mi:link" className="text-lg"/>}
                    loading={submitting}
                    onClick={onConfirm}
                />
            </div>
            <span className="text-xs text-500">
                {selectedCount > 0
                    ? translate("tasks-selected-count").replace("{{count}}", new Intl.NumberFormat("fa-IR").format(selectedCount))
                    : translate("no-tasks-selected")
                }
            </span>
        </div>
    );
}

export default SelectTasksDialog;
